import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { paintingAPI } from '../utils/api';
import { pixelEvents } from '../utils/pixelEvents';
import SEO from '../components/SEO';
import config from '../config/config';
import './Gallery.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Gallery = () => {
  const [searchParams] = useSearchParams();
  const [paintings, setPaintings] = useState([]);
  const [filteredPaintings, setFilteredPaintings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [showContactForm, setShowContactForm] = useState(false);
  const [selectedPainting, setSelectedPainting] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  
  const categories = ['all', 'Abstract', 'Portrait', 'Landscape', 'Modern', 'Classical'];

  useEffect(() => {
    fetchPaintings();
  }, []);

  useEffect(() => {
    if (selectedCategory === 'all') {
      setFilteredPaintings(paintings);
    } else {
      setFilteredPaintings(paintings.filter(p => p.category === selectedCategory));
    }
  }, [selectedCategory, paintings]);

  const fetchPaintings = async () => {
    try {
      const response = await paintingAPI.getAll({ status: 'available' });
      setPaintings(response.data);
      setFilteredPaintings(response.data);
    } catch (error) {
      console.error('Error fetching paintings:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleContactClick = (painting) => {
    pixelEvents.contactToBuy(painting.title, painting.id, painting.price, painting.category);
    setSelectedPainting(painting);
    setShowContactForm(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address.');
      return;
    }
  
    // Validate phone number (Indian format: 10 digits starting with 6-9)
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(formData.phone)) {
      toast.error('Please enter a valid 10-digit Indian phone number.');
      return;
    }
  
    try {
       const dataToSubmit = {
        paintingId: selectedPainting.id,
        paintingTitle: selectedPainting.title,
        ...formData,
      };
      await paintingAPI.contactSubmit(dataToSubmit);
      toast.success('Your interest has been submitted. We will contact you soon.');
      setShowContactForm(false);
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to submit your interest. Please try again.');
    }
  };

  const gallerySchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Art Gallery - Canvaart",
    "description": "Browse our collection of original canvas paintings for sale. Abstract art, portraits, landscapes, and modern artwork by Aanishka Chowdhury.",
    "url": "https://canvaart.in/gallery",
    "isPartOf": {
      "@type": "WebSite",
      "name": "Canvaart",
      "url": "https://canvaart.in"
    },
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": filteredPaintings.map((painting, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Painting",
          "name": painting.title,
          "url": `https://canvaart.in/painting/${painting.slug || painting.id}`,
          "image": `${config.BASE_API_URL}${painting.images[0]}`,
          "artform": painting.category,
          "offers": {
            "@type": "Offer",
            "price": painting.price,
            "priceCurrency": "INR",
            "availability": "https://schema.org/InStock"
          }
        }
      }))
    }
  };

  return (
    <div className="gallery-page">
      <SEO 
        title="Buy Original Canvas Paintings Online India | Art Gallery for Sale"
        description="Buy original canvas paintings online in India. Hand-painted abstract art, portraits, landscapes for sale. Unique artwork by Aanishka Chowdhury. Free shipping above ₹5000."
        keywords="buy paintings online India, original canvas art for sale, hand painted paintings, abstract art India, portrait paintings for sale, landscape paintings, modern art India, buy artwork online, canvas wall art, home decor paintings"
        url="/gallery"
        schema={gallerySchema}
      />
      
      <ToastContainer />
      
      <header className="gallery-header">
        <div className="container">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Art Gallery
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Browse through our collection of unique canvas paintings
          </motion.p>
        </div>
      </header>

      <section className="container" aria-label="Painting Gallery">
        <nav className="category-filter" aria-label="Category filter">
          {categories.map((category) => (
            <button
              key={category}
              className={`filter-btn ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category)}
              aria-pressed={selectedCategory === category}
              aria-label={`Filter by ${category}`}
            >
              {category}
            </button>
          ))}
        </nav>

        {loading ? (
          <div className="spinner" role="status" aria-label="Loading paintings"></div>
        ) : filteredPaintings.length === 0 ? (
          <div className="no-paintings">
            <p>No paintings found in this category.</p>
          </div>
        ) : (
          <div className="paintings-grid" role="list">
            {filteredPaintings.map((painting, index) => (
              <motion.article
                key={painting.id}
                className="painting-card"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                role="listitem"
                itemScope
                itemType="https://schema.org/Painting"
              >
                <div className="painting-imageGal">
                  <img 
                    src={`${config.BASE_API_URL}${painting.images[0]}`} 
                    alt={`${painting.title} - Original ${painting.category} canvas painting`}
                    loading="lazy"
                    itemProp="image"
                  />
                  <div className="painting-overlay">
                    <Link to={`/painting/${painting.slug || painting.id}`} className="btn-view" aria-label={`View details of ${painting.title}`}>View Details</Link>
                  </div>
                </div>
                <div className="painting-info">
                  <h3 itemProp="name">{painting.title}</h3>
                  <p className="painting-category" itemProp="artform">{painting.category}</p>
                  <div className="painting-footer">
                    <p className="painting-price" itemProp="offers" itemScope itemType="https://schema.org/Offer">
                      <span itemProp="priceCurrency">INR</span>
                      <span itemProp="price">₹{painting.price}</span>
                    </p>
                    <span className="painting-size">Proportion: {painting.size}</span>
                  </div>
                  <div className='btnHolder'>
                    <button className="btn-contact" onClick={() => handleContactClick(painting)} aria-label={`Contact to buy ${painting.title}`}>
                      Contact to Buy
                    </button>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </section>

      {showContactForm && (
        <div className="contact-form-modal" role="dialog" aria-modal="true" aria-labelledby="contact-form-title">
          <div className="contact-form-content">
            <h2 id="contact-form-title">Contact to Buy: {selectedPainting.title}</h2>
            <form onSubmit={handleSubmit}>
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleInputChange}
                required
                aria-label="Your name"
              />
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                value={formData.email}
                onChange={handleInputChange}
                required
                aria-label="Your email address"
              />
              <input
                type="number"
                name="phone"
                minLength={10}
                maxLength={10}
                placeholder="Your Phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                aria-label="Your phone number"
              />
              <textarea
                name="message"
                placeholder="Your Message"
                value={formData.message}
                onChange={handleInputChange}
                required
                aria-label="Your message"
              ></textarea>
              <button type="submit" className="btn-submit">Submit</button>
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setShowContactForm(false)}
              >
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;